import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentUploadZone = ({ onFileSelect, isUploading, uploadProgress }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e?.dataTransfer?.files);
    if (files?.length > 0) {
      onFileSelect(files);
    }
  };

  const handleFileInput = (e) => {
    const files = Array.from(e?.target?.files);
    if (files?.length > 0) {
      onFileSelect(files);
    }
  };

  const handleClick = () => {
    fileInputRef?.current?.click();
  };

  const acceptedFormats = '.pdf,.doc,.docx,.jpg,.jpeg,.png';

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-elevation-2">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg" title="Upload">
          <Icon name="Upload" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">Upload Documents</h3>
          <p className="text-sm text-muted-foreground">Add compliance documents to your sites</p>
        </div>
      </div>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        className={`relative border-2 border-dashed rounded-lg p-8 md:p-12 text-center cursor-pointer transition-smooth ${
          isDragging 
            ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50 hover:bg-muted/50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedFormats}
          onChange={handleFileInput}
          className="hidden"
          disabled={isUploading}
        />

        {isUploading ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <Icon name="Loader2" size={48} className="text-accent animate-spin" />
            </div>
            <div>
              <p className="font-medium text-foreground mb-2">Uploading documents...</p>
              <div className="w-full max-w-xs mx-auto bg-muted rounded-full h-2 overflow-hidden">
                <div 
                  className="bg-accent h-full transition-smooth"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">{uploadProgress}% complete</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full">
                <Icon name="FileUp" size={32} color="var(--color-accent)" />
              </div>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">
                Drag and drop files here, or click to browse
              </p>
              <p className="text-sm text-muted-foreground">
                Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB per file)
              </p>
            </div>
            <Button variant="outline" iconName="Upload" iconPosition="left">
              Choose Files
            </Button>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md">
          <Icon name="FileText" size={16} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">PDF</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md">
          <Icon name="FileText" size={16} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">DOC</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md">
          <Icon name="Image" size={16} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">JPG/PNG</span>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadZone;